import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Checkbox,
  DatePicker,
  Row,
  Col,
  Input,
  Button,
  Typography,
  Icon
} from "antd";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import styled, { ThemedStyledProps } from "styled-components";
import { push } from "connected-react-router";
import { RangePickerProps } from "antd/lib/date-picker/interface";
import { find } from "lodash";

import { MainComponentContainer } from "../../../shared/MainComponentContainer";
import { EntryComponentContainer } from "../../../shared/EntryComponentContainer";
import TimeWindowPicker from "../../../shared/TimeWindowPicker";
import RowWrapper from "../../../shared/RowWrapper";
import { VehicleSelect } from "../../../shared/VehicleSelect";
import { ExtrasPicker } from "../../../shared/ExtrasPicker";
import { AddressInput } from "../../addresses/components/AddressInput";
import { RootState } from "../../../store/rootReducer";
import { updateOrder, resetOrder } from "../actions";
import { fetchAddressesAsync } from "../../addresses/actions";
import { ApiClient } from "../../../lib/api";
import { BookRecurringOrder } from "../model/BookRecurringOrder";
import { AddressValidator } from "../../../lib/validators/AddressValidator";
import { AddressState } from "../../../lib/model/AddressState";

const client = new ApiClient();

const CheckboxGroup = Checkbox.Group;
const { RangePicker } = DatePicker;
const { Paragraph } = Typography;

const weekdayOptions = [
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
  "Sonntag"
];

const EntryHeadline = styled.span`
  color: #061a32;
  font-size: 14px;
  line-height: 17px;
  font-weight: 400;
`;

const WeekDaysInput = styled(CheckboxGroup)`
  &.ant-checkbox-group {
    margin: 15px 0;
  }
`;

export const StyledInput = styled(Input)`
  height: 40px !important;
  background-color: #f2f2f2 !important;
  border: none !important;
  font-size: 11px !important;
  line-height: 15px !important;
  color: #386ee8 !important;
  font-weight: 300 !important;
  &::placeholder {
    color: #386ee8 !important;
    font-weight: 300 !important;
  }
`;

const ContactLink = styled.a`
  font-weight: 400;
  font-size: 13px;
  line-height: 17px;
  color: #386ee8;
  margin-bottom: 2px;
  &:hover {
    color: #061a32;
  }
`;

const StyledRangePicker = styled(RangePicker)`
  .ant-calendar-picker-input.ant-input {
    border: ${(
      props: ThemedStyledProps<
        RangePickerProps &
          React.RefAttributes<React.Component<RangePickerProps, any, any>>,
        any
      > & { hasError: boolean }
    ) => (props.hasError ? "1px solid #eb0333 !important" : "none !important")};
    background: #f2f2f2 !important;
    margin-top: 10px;
  }
`;

const addressValidator = new AddressValidator();

const isValidOrder = (order: BookRecurringOrder): boolean => {
  const { startAddress, endAddress, vehicle, startDate, endDate } = order;
  const addressesValid = [startAddress, endAddress].reduce(
    (acc, state) => acc && addressValidator.isValidState(state),
    true
  );

  return !!startDate && !!endDate && !!vehicle && addressesValid;
};

type State = {
  checkedDays: CheckboxValueType[];
  isDaysKnown: boolean;
  isCreatingOrder: boolean;
};

const mapStateToProps = (state: RootState) => ({
  order: state.bookRecurring.order,
  addresses: state.addresses.addresses,
  isLoadingAddresses: state.addresses.isLoadingAddresses
});

const dispatchProps = {
  push,
  updateOrder,
  resetOrder,
  fetchAddresses: fetchAddressesAsync.request
};

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps;

class RecurringContainer extends Component<Props, State> {
  readonly state: State = {
    checkedDays: [],
    isDaysKnown: true,
    isCreatingOrder: false
  };

  componentDidMount = () => {
    this.props.fetchAddresses();
  };

  handleBookingClicked = async () => {
    const { addresses, order, resetOrder } = this.props;
    const startAddress = order.startAddress.id
      ? (find(addresses, ["id", order.startAddress.id]) as AddressState)
      : (order.startAddress.create as AddressState);
    const endAddress = order.endAddress.id
      ? (find(addresses, ["id", order.endAddress.id]) as AddressState)
      : (order.endAddress.create as AddressState);
    this.setState({ isCreatingOrder: true });
    await client.createRecurringOrder({
      ...order,
      startAddress,
      endAddress
    });
    resetOrder();
    this.props.push("/recurring-success");
    this.setState({ isCreatingOrder: false });
  };

  onWeekdayChange = (days: CheckboxValueType[]) => {
    const { order, updateOrder } = this.props;

    this.setState({ checkedDays: days }, () => {
      updateOrder({ ...order, days: days.map(day => day.toString()) });
    });
  };

  onCheckDays = () => {
    const { order, updateOrder } = this.props;
    const { isDaysKnown, checkedDays } = this.state;

    const days = !isDaysKnown ? checkedDays.map(day => day.toString()) : [];

    this.setState({ isDaysKnown: !isDaysKnown }, () => {
      updateOrder({ ...order, days });
    });
  };

  render() {
    const { order, updateOrder } = this.props;
    const { isDaysKnown, isCreatingOrder } = this.state;

    return (
      <MainComponentContainer>
        <RowWrapper type="flex" justify="center">
          <Col span={24}>
            <EntryComponentContainer>
              <EntryHeadline style={{ marginBottom: 12 }}>
                Regelmäßige Fahrten mit Forward Recurring
              </EntryHeadline>
              <Paragraph>
                Profitieren Sie von unseren attraktiven Raten und der Forward
                Technologie auch bei regelmäßigen Fahrten, Dauer- sowie
                Festeinsätzen. Ob mehrmals täglich oder nur einmal wöchentlich,
                ob Lagerumfuhr, Verteiler- und Sammelverkehre oder
                Kundenauslieferungen mit Forward Recurring bieten wir Ihnen
                Transporte zu attraktiven, fixen Volumenraten.
              </Paragraph>
              <Paragraph>
                Geben Sie hier die Daten Ihrer regelmäßigen Transporte ein. Für
                regelmäßige Fahrten mit besonderen Anforderungen oder mehreren
                bzw. noch unbekannten Ladestellen freuen wir uns über Ihre
                telefonische oder schriftliche Anfrage.
              </Paragraph>
              <ContactLink href="tel:+494087409121">
                <Icon type="phone" /> 040 874 091 21
              </ContactLink>
              <ContactLink href="mailto:mail@forward-cargo.com">
                <Icon type="mail" /> mail@forward-cargo.com
              </ContactLink>
            </EntryComponentContainer>
          </Col>
          <Col span={24}>
            <EntryComponentContainer>
              <EntryHeadline>Zeitraum</EntryHeadline>
              <StyledRangePicker
                onChange={values =>
                  updateOrder({
                    ...order,
                    startDate: values[0] ? values[0].toISOString() : "",
                    endDate: values[1] ? values[1].toISOString() : ""
                  })
                }
                hasError={!order.startDate || !order.endDate}
                format="DD.MM.YYYY"
              />
              <WeekDaysInput
                disabled={!isDaysKnown}
                options={weekdayOptions}
                onChange={this.onWeekdayChange}
              />
              <Checkbox checked={!isDaysKnown} onChange={this.onCheckDays}>
                Wochentage noch nicht bekannt
              </Checkbox>
            </EntryComponentContainer>
          </Col>
          <Col span={24}>
            <Row type="flex" gutter={21} style={{ marginBottom: 18 }}>
              <Col span={12} md={12} xs={24}>
                <EntryComponentContainer
                  style={{ height: "100%", marginBottom: 0 }}
                >
                  <EntryHeadline>Beladung</EntryHeadline>
                  <AddressInput
                    title="* Beladestelle"
                    value={order.startAddress}
                    onChange={value =>
                      updateOrder({ ...order, startAddress: value })
                    }
                  />
                  <div style={{ height: "21px" }} />
                  <TimeWindowPicker
                    value={order.startWindow}
                    onChange={value =>
                      updateOrder({ ...order, startWindow: value })
                    }
                  />
                </EntryComponentContainer>
              </Col>
              <Col span={12} md={12} xs={24}>
                <EntryComponentContainer
                  style={{ height: "100%", marginBottom: 0 }}
                >
                  <EntryHeadline>Entladung</EntryHeadline>
                  <AddressInput
                    title="* Entladestelle"
                    value={order.endAddress}
                    onChange={value =>
                      updateOrder({ ...order, endAddress: value })
                    }
                  />
                  <div style={{ height: "21px" }} />
                  <TimeWindowPicker
                    value={order.endWindow}
                    onChange={value =>
                      updateOrder({ ...order, endWindow: value })
                    }
                  />
                </EntryComponentContainer>
              </Col>
            </Row>
          </Col>
          <Col span={12} md={12} xs={24}>
            <EntryComponentContainer>
              <EntryHeadline>Fahrzeug</EntryHeadline>
              <VehicleSelect
                value={order.vehicle}
                onSelect={value => updateOrder({ ...order, vehicle: value })}
              />
            </EntryComponentContainer>
          </Col>
          <Col span={24}>
            <Row type="flex" gutter={21}>
              <Col span={12} md={12} xs={24}>
                <EntryComponentContainer>
                  <EntryHeadline>Fahrzeugoptionen</EntryHeadline>
                  <ExtrasPicker
                    activeValues={order.extras}
                    onChange={value => updateOrder({ ...order, extras: value })}
                  />
                </EntryComponentContainer>
              </Col>
              <Col span={12} md={12} xs={24} style={{ display: "flex" }}>
                <EntryComponentContainer
                  style={{ justifyContent: "flex-end", alignItems: "flex-end" }}
                >
                  <Button
                    type="primary"
                    disabled={!isValidOrder(order)}
                    loading={isCreatingOrder}
                    onClick={this.handleBookingClicked}
                    style={{
                      backgroundColor: "#386ee8 !important",
                      border: "none !important",
                      fontWeight: 300,
                      borderRadius: 0,
                      height: "36px",
                      width: "150px"
                    }}
                  >
                    Angebot anfordern
                  </Button>
                </EntryComponentContainer>
              </Col>
            </Row>
          </Col>
        </RowWrapper>
      </MainComponentContainer>
    );
  }
}

export default connect(
  mapStateToProps,
  dispatchProps
)(RecurringContainer);
